import {
  computed,
  inject,
  provide,
  ref,
  unref,
  watch,
  watchEffect,
  getCurrentInstance as _getCurrentInstance
} from 'vue';

import { cdsLight, cdsDark, b2bLight, b2bDark, promoLight, promoDark } from '@components/themes';
import {
  createRange,
  darken,
  HexToRGB,
  getCurrentInstance,
  IN_BROWSER,
  lighten,
  mergeDeep,
  parseColor,
  propsFactory,
  RGBtoHex
} from '@components/utils';
import colorsPalette from '@components/themes/colors';

import type { TThemes } from '@components/themes';
import type { App, DeepReadonly, InjectionKey, PropType, Ref, ComputedRef } from 'vue';
import type { Hex } from '@components/utils';

type DeepPartial<T> = T extends object ? { [P in keyof T]?: DeepPartial<T[P]> } : T;

export type IThemeOptions =
  | false
  | {
      cspNonce?: string;
      defaultTheme?: TThemes | string;
      variations?: false | VariationOptions;
      themes?: Record<string, ThemeDefinition>;
      /**
       * @deprecated НЕ ИСПОЛЬЗУЙТЕ НИ В КОЕМ СЛУЧАЕ, если у вас нет web компонентов с shadow DOM
       * * ЭТО КОСТЫЛЬ!!!!
       * * Указывает, что все стили cds-style-sheets будут прикреплены к первому элементу в shadowRoot DOM.
       * * Если у вас нет web компонентов с shadow DOM, не используйте эту опцию НИ В КОЕМ СЛУЧАЕ!!!
       */
      isShadowDom?: boolean;
    };

export type ThemeDefinition = DeepPartial<InternalThemeDefinition>;

export type TTheme = typeof availableTheme[number];

export interface Colors extends BaseColors {
  [key: string]: string;
}

interface InternalThemeOptions {
  cspNonce?: string;
  isDisabled: boolean;
  defaultTheme: string;
  variations: false | VariationOptions;
  themes: Record<string, InternalThemeDefinition>;
  isShadowDom?: boolean;
}

interface VariationOptions {
  colors: string[];
  lighten: number;
  darken: number;
}

interface InternalThemeDefinition {
  dark: boolean;
  colors: Colors;
  variables: Record<string, string | number>;
}

interface BaseColors {
  'brand-primary': string;
  'brand-primary-active': string;
  'brand-primary-border': string;
  'brand-primary-content': string;
  'brand-primary-hover': string;
  'brand-secondary': string;
  'brand-secondary-active': string;
  'brand-secondary-border': string;
  'brand-secondary-hover': string;
}

export interface ThemeInstance {
  readonly isDisabled: boolean;
  readonly themes: Ref<Record<string, InternalThemeDefinition>>;
  readonly name: Readonly<Ref<string>>;
  readonly current: DeepReadonly<Ref<InternalThemeDefinition>>;
  readonly computedThemes: DeepReadonly<Ref<Record<string, InternalThemeDefinition>>>;
  readonly themeClasses: Readonly<ComputedRef<string | undefined>>;
  readonly styles: Readonly<ComputedRef<string>>;
  readonly global: {
    readonly name: Ref<string>;
    readonly current: DeepReadonly<Ref<InternalThemeDefinition>>;
  };
}

export const ThemeSymbol: InjectionKey<ThemeInstance> = Symbol.for('cds:theme');

const deprecatedTheme = ['cds', 'b2b', 'dark', 'promo'];

export const availableTheme = ['cdsLight', 'cdsDark', 'b2bLight', 'b2bDark', 'promoLight', 'promoDark'] as const;
export const makeThemeProps = propsFactory(
  {
    /**
     * Тема
     */
    theme: {
      type: String as PropType<TTheme | string>,
      available: availableTheme,
      default: undefined
    }
  },
  'theme'
);

const DEFAULT_THEME: TThemes = 'cdsLight';

const defaultThemeOptions: Exclude<IThemeOptions, false> = {
  defaultTheme: DEFAULT_THEME,
  variations: false,
  themes: { cdsLight, cdsDark, b2bLight, b2bDark, promoLight, promoDark }
};

export function createTheme(options?: IThemeOptions): ThemeInstance & { install: (app: App) => void } {
  const parsedOptions = parseThemeOptions(options);
  const name = ref(parsedOptions.defaultTheme);
  const themes = ref(parsedOptions.themes);

  const computedThemes = computed(() => {
    const acc: Record<string, InternalThemeDefinition> = {};

    for (const [name, original] of Object.entries(unref(themes))) {
      const theme: InternalThemeDefinition = (acc[name] = {
        ...original,
        colors: {
          ...original.colors
        }
      });

      // Generate color variants
      if (parsedOptions.variations) {
        for (const name of parsedOptions.variations.colors) {
          const color = theme.colors[name];

          if (!color) continue;

          for (const variation of ['lighten', 'darken'] as const) {
            const fn = variation === 'lighten' ? lighten : darken;

            for (const amount of createRange(parsedOptions.variations[variation], 1)) {
              theme.colors[`${name}-${variation}-${amount}`] = RGBtoHex(fn(parseColor(color), amount));
            }
          }
        }
      }
    }

    return acc;
  });

  const current = computed(() => unref(computedThemes)[getAvailableThemeName(unref(name))]);

  const styles = computed(() => {
    const lines: string[] = [];

    if (unref(current).dark) {
      createCssClass(lines, ':root', ['color-scheme: dark']);
    }

    createCssClass(lines, ':root', genCssVariables(unref(current)));

    for (const [themeName, theme] of Object.entries(unref(computedThemes))) {
      createCssClass(lines, `.cds-theme--${themeName}`, [
        `color-scheme: ${theme.dark ? 'dark' : 'normal'}`,
        ...genCssVariables(theme)
      ]);
    }

    const bgLines: string[] = [];
    const fgLines: string[] = [];

    const colors = new Set(Object.values(unref(computedThemes)).flatMap((theme) => Object.keys(theme.colors)));

    for (const key of colors) {
      if (/^on-[a-z]/.test(key)) {
        createCssClass(fgLines, `.${key}`, [`color: rgb(var(--cds-color-${key})) !important`]);
      } else {
        createCssClass(bgLines, `.cds-bg-${key}`, [
          `--cds-color-overlay-multiplier: var(--cds-color-${key}-overlay-multiplier)`,
          `background-color: rgb(var(--cds-color-${key})) !important`,
          `color: rgb(var(--cds-color-on-${key})) !important`
        ]);
        createCssClass(fgLines, `.cds-text-${key}`, [`color: rgb(var(--cds-color-${key})) !important`]);
        createCssClass(fgLines, `.cds-border-${key}`, [`border-color: rgb(var(--cds-color-${key}))`]);
      }
    }

    lines.push(...bgLines, ...fgLines);

    return lines.map((str, i) => (i === 0 ? str : `    ${str}`)).join('');
  });

  function getHead() {
    return {
      style: [
        {
          children: unref(styles),
          id: 'cds-theme-stylesheet',
          nonce: parsedOptions.cspNonce || (false as never)
        }
      ]
    };
  }

  function install(app: App) {
    if (parsedOptions.isDisabled) return;

    const head = app._context.provides.usehead;

    if (head) {
      if (head.push) {
        const entry = head.push(getHead);

        if (IN_BROWSER) {
          watch(styles, () => entry.patch(getHead));
        }
      } else {
        if (IN_BROWSER) {
          head.addHeadObjs(computed(getHead));
          watchEffect(() => head.updateDOM());
        } else {
          head.addHeadObjs(getHead());
        }
      }
    } else {
      if (parsedOptions.isShadowDom) {
        /**
         * Костыль для работы с shadow DOM и web компонентами
         * Костыль реализован по просьбе из-за гребанных констыльных виджетов в shadow DOM, которые используют разные темы и разные версии CDS на одной странице
         * Работать будет только если корневой элемент приложения прикреплен к shadow DOM (через attachedRoot)
         * Если у вас нет web компонентов с shadow DOM, не используйте эту опцию
         */
        injectStylesVariablesForShadowDom(styles);
      } else {
        let styleEl = IN_BROWSER ? document.getElementById('cds-theme-stylesheet') : null;

        const updateStyles = () => {
          if (typeof document !== 'undefined' && !styleEl) {
            const el = document.createElement('style');
            el.type = 'text/css';
            el.id = 'cds-theme-stylesheet';
            if (parsedOptions.cspNonce) el.setAttribute('nonce', parsedOptions.cspNonce);
            styleEl = el;
            document.head.appendChild(styleEl);
          }

          if (styleEl) styleEl.innerHTML = unref(styles);
        };

        if (IN_BROWSER) {
          watch(styles, updateStyles, { immediate: true });
        } else {
          updateStyles();
        }
      }
    }
  }

  const themeClasses = computed(() => (parsedOptions.isDisabled ? undefined : `cds-theme--${unref(name)}`));

  return {
    install,
    isDisabled: parsedOptions.isDisabled,
    name,
    themes,
    current,
    computedThemes,
    themeClasses,
    styles,
    global: {
      name,
      current
    }
  };
}

export function provideTheme(props: { theme?: string }) {
  getCurrentInstance('provideTheme');

  const theme = inject(ThemeSymbol, null);

  if (!theme) throw new Error('Could not find CDS theme injection');

  const name = computed<string>(() => {
    return props.theme ?? unref(theme.name);
  });
  const current = computed(() => unref(theme.themes)[unref(name)]);

  const themeClasses = computed(() => (theme.isDisabled ? undefined : `cds-theme--${unref(name)}`));

  const newTheme: ThemeInstance = {
    ...theme,
    name,
    current,
    themeClasses
  };

  provide(ThemeSymbol, newTheme);

  return newTheme;
}

export function useTheme() {
  getCurrentInstance('useTheme');

  const theme = inject(ThemeSymbol, null);

  if (!theme) throw new Error('Could not find CDS theme injection');

  return theme;
}

function parseThemeOptions(options: IThemeOptions = defaultThemeOptions): InternalThemeOptions {
  if (!options) return { ...defaultThemeOptions, isDisabled: true } as InternalThemeOptions;

  const themes: Record<string, InternalThemeDefinition> = {};

  options.defaultTheme = getAvailableThemeName(options.defaultTheme);

  for (const [key, theme] of Object.entries(options.themes ?? {})) {
    const defaultTheme =
      theme.dark || key.toLowerCase().includes('dark')
        ? defaultThemeOptions.themes?.cdsDark
        : defaultThemeOptions.themes?.cdsLight;
    themes[key] = mergeDeep(defaultTheme, theme) as InternalThemeDefinition;
  }

  return mergeDeep(defaultThemeOptions, { ...options, themes }) as InternalThemeOptions;
}

function createCssClass(lines: string[], selector: string, content: string[]) {
  lines.push(`${selector} {\n`, ...content.map((line) => `  ${line};\n`), '}\n');
}

function genCssVariables(theme: InternalThemeDefinition) {
  const variables: string[] = [];

  // Theme colors
  for (const [key, value] of Object.entries(theme.colors)) {
    const rgb = parseColor(value);
    const variableValue = rgb.a !== undefined ? `${rgb.r},${rgb.g},${rgb.b},${rgb.a}` : `${rgb.r},${rgb.g},${rgb.b}`;

    variables.push(`--cds-color-${key}: ${variableValue}`);
  }

  // Theme variables
  for (const [key, value] of Object.entries(theme.variables)) {
    const color = typeof value === 'string' && value.startsWith('#') ? parseColor(value) : undefined;
    const rgb = color ? `${color.r}, ${color.g}, ${color.b}` : undefined;

    variables.push(`--cds-${key}: ${rgb ?? value}`);
  }

  // Alpha colors from palette
  for (const [key, value] of Object.entries(colorsPalette.alpha)) {
    const rgb = HexToRGB(value as Hex);
    const paramKey = key.replace(/([a-z])([0-9])/gi, '$1-$2');

    variables.push(`--cds-alpha-${paramKey}: ${rgb.r},${rgb.g},${rgb.b},${rgb.a}`);
  }

  return variables;
}

function getAvailableThemeName(theme?: string): TTheme | string {
  if (theme && deprecatedTheme.includes(theme)) {
    console.warn(
      `[CDS] The theme "${theme}" is deprecated. Please use one of the following themes: ${availableTheme.join(', ')}`
    );

    if (theme === 'cds' || theme === 'promo') {
      return 'cdsLight';
    } else if (theme === 'b2b') {
      return 'b2bLight';
    } else if (theme === 'dark') {
      return 'cdsDark';
    } else {
      return 'cdsLight';
    }
  }

  return theme || DEFAULT_THEME;
}

/**
 *  * НЕ ИСПОЛЬЗУЙТЕ НИ В КОЕМ СЛУЧАЕ, если у вас нет web компонентов с shadow DOM
 *  * ЭТО КОСТЫЛЬ!!!!
 *  * Если у вас нет web компонентов с shadow DOM, не используйте эту опцию НИ В КОЕМ СЛУЧАЕ!!!
 *  */
function injectStylesVariablesForShadowDom(styles: ComputedRef<string>) {
  const vm = _getCurrentInstance();
  const root = (vm as any)?.ce.shadowRoot as ShadowRoot;

  if (!root) {
    console.warn('[CDS] Could not find shadow root for theme styles');
    return;
  }

  let styleEl = IN_BROWSER ? root.getElementById('cds-theme-stylesheet') : null;

  const updateStyles = () => {
    if (typeof document !== 'undefined' && !styleEl) {
      const el = document.createElement('style');
      el.type = 'text/css';
      el.id = 'cds-theme-stylesheet';
      styleEl = el;
      root.appendChild(el);
    }

    if (styleEl) styleEl.innerHTML = unref(styles);
  };

  if (IN_BROWSER) {
    watch(styles, updateStyles, { immediate: true });
  } else {
    updateStyles();
  }
}
